/**
 * NotificationToast Component
 *
 * Real-time toast popup for incoming notifications.
 * Appears in the top-right corner when new notifications arrive.
 *
 * Features:
 * - Animated slide-in from top-right
 * - Auto-dismiss after 5 seconds
 * - Manual dismiss button
 * - Click to navigate to notification action
 * - Icon based on notification type and urgency
 * - Multiple toasts stacked vertically
 *
 * Epic: MVP 1.21.9 - Dashboard Design System & Notifications (SCRUM-459)
 *
 * @example
 * <NotificationToast notification={notification} onDismiss={handleDismiss} />
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BellIcon as _BellIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

/**
 * Get icon component based on notification type and urgency
 * @param {string} type - Notification type
 * @param {string} urgency - Notification urgency
 * @returns {React.Component} Icon component
 */
function getToastIcon(type, urgency) {
  // Critical/urgent notifications
  if (urgency === 'critical' || urgency === 'high') {
    return ExclamationCircleIcon;
  }

  // Type-specific icons
  const iconMap = {
    booking_confirmed: CheckCircleIcon,
    booking_cancelled: ExclamationTriangleIcon,
    payment_received: CheckCircleIcon,
    payment_failed: ExclamationCircleIcon,
    system_alert: ExclamationTriangleIcon,
    admin_alert: ExclamationCircleIcon,
  };

  // eslint-disable-next-line security/detect-object-injection -- safe: type is enum from database
  return iconMap[type] || InformationCircleIcon;
}

/**
 * Get toast background color based on urgency
 * @param {string} urgency - Notification urgency
 * @returns {string} Tailwind background class
 */
function getToastColor(urgency) {
  const colorMap = {
    critical: 'bg-red-50 border-red-500',
    high: 'bg-orange-50 border-orange-500',
    medium: 'bg-blue-50 border-blue-500',
    low: 'bg-gray-50 border-gray-500',
  };

  // eslint-disable-next-line security/detect-object-injection -- safe: urgency is enum from database
  return colorMap[urgency] || 'bg-blue-50 border-blue-500';
}

/**
 * Get icon color based on urgency
 * @param {string} urgency - Notification urgency
 * @returns {string} Tailwind text color class
 */
function getIconColor(urgency) {
  const colorMap = {
    critical: 'text-red-600',
    high: 'text-orange-600',
    medium: 'text-blue-600',
    low: 'text-gray-600',
  };

  // eslint-disable-next-line security/detect-object-injection -- safe: urgency is enum from database
  return colorMap[urgency] || 'text-blue-600';
}

/**
 * NotificationToast Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.notification - Notification object
 * @param {Function} props.onDismiss - Callback when dismissing toast
 * @param {number} props.autoDismissMs - Auto-dismiss delay in ms (default: 5000)
 * @param {number} props.index - Toast index for stacking (default: 0)
 */
export default function NotificationToast({
  notification,
  onDismiss,
  autoDismissMs = 5000,
  index = 0,
}) {
  const navigate = useNavigate();
  const [isExiting, setIsExiting] = useState(false);

  const Icon = getToastIcon(notification.type, notification.urgency);
  const toastColor = getToastColor(notification.urgency);
  const iconColor = getIconColor(notification.urgency);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  /**
   * Handle dismiss with exit animation
   */
  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      if (onDismiss) {
        onDismiss(notification.id);
      }
    }, 300); // Match animation duration
  }, [onDismiss, notification.id]);

  // ============================================================================
  // AUTO-DISMISS TIMER
  // ============================================================================

  useEffect(() => {
    if (autoDismissMs > 0) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, autoDismissMs);

      return () => clearTimeout(timer);
    }
  }, [autoDismissMs, handleDismiss]);

  /**
   * Handle toast click - navigate and dismiss
   */
  const handleClick = () => {
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
    handleDismiss();
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  // Calculate vertical position based on index (stacking)
  const topPosition = 16 + index * 100; // 16px base + 100px per toast

  return (
    <div
      className={`
        fixed right-4 z-50 w-96 max-w-full
        bg-white rounded-lg shadow-lg border-l-4
        transform transition-all duration-300 ease-in-out
        ${toastColor}
        ${isExiting ? 'translate-x-[400px] opacity-0' : 'translate-x-0 opacity-100'}
        ${notification.actionUrl ? 'cursor-pointer hover:shadow-xl' : ''}
      `}
      style={{ top: `${topPosition}px` }}
      onClick={notification.actionUrl ? handleClick : undefined}
    >
      <div className="p-4 flex items-start gap-3">
        {/* Icon */}
        <div className={`flex-shrink-0 ${iconColor}`}>
          <Icon className="w-6 h-6" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <p className="text-sm font-semibold text-gray-900">{notification.title}</p>

          {/* Message */}
          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{notification.message}</p>

          {/* Action label */}
          {notification.actionLabel && notification.actionUrl && (
            <p className="text-sm font-medium text-brand-primary mt-2">{notification.actionLabel}</p>
          )}
        </div>

        {/* Dismiss button */}
        <button
          type="button"
          onClick={(  _e) => {
            _e.stopPropagation(); // Prevent toast click
            handleDismiss();
          }}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Dismiss notification"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Progress bar (auto-dismiss indicator) */}
      {autoDismissMs > 0 && (
        <div className="h-1 bg-gray-200 rounded-b-lg overflow-hidden">
          <div
            className={`h-full ${
              notification.urgency === 'critical'
                ? 'bg-red-500'
                : notification.urgency === 'high'
                ? 'bg-orange-500'
                : 'bg-blue-500'
            } transition-all ease-linear`}
            style={{
              width: '100%',
              animation: `shrink ${autoDismissMs}ms linear`,
            }}
          />
        </div>
      )}

      {/* Inline styles for progress bar animation */}
      <style>{`
        @keyframes shrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </div>
  );
}
